import { SIDE_RAIL_SECTION_KEY, state } from "../state";
import { setStatus } from "../ui/status";
import { setProgress, hideProgress } from "../ui/progress";
import { buildElementMetaCache } from "./meta";
import { getIfcFileCacheSignature } from "./metaCache";
import {
  applyFilterStateToUi,
  rebuildMeshVisibility,
  resetFilterUiAndState,
  setFilterControlsEnabled,
  updateTransparentSpaceIdsFromMeta,
} from "./filters";
import { buildRevisionSnapshotFromFile, buildRevisionSnapshotFromModel } from "./revision";
import { parseIfcRevisionSnapshot } from "./compare";
import { buildIfcTree, renderIfcTree } from "../features/tree";
import { refreshPropertiesPanel } from "../features/properties";
import { loadViewsForCurrentFile } from "../features/views";
import { tryApplyShareLinkFromLocationHash } from "../features/shareLink";
import { loadAnnotationsForCurrentFile, refreshAnnotationMarkers, updateAnnotationUi } from "../features/annotations";
import { updateSearchUiState } from "../features/search";
import { refreshPlanUi } from "../features/planView";
import { syncColorRuleUi } from "../features/colorRules";
import type { FilterState } from "../state";

let metaPromise: Promise<void> | null = null;
let treePromise: Promise<void> | null = null;
let revisionPromise: Promise<void> | null = null;
let lastCacheSignature: string | null = null;
let lastInnerFile: File | null = null;
let lastModelId: string | null = null;
let pendingFilterState: FilterState = { storeyId: "", types: [], mark: "" };
let shareLinkPending = false;

export function resetDeferredInitState(): void {
  metaPromise = null;
  treePromise = null;
  revisionPromise = null;
  lastCacheSignature = null;
  lastInnerFile = null;
  lastModelId = null;
  shareLinkPending = false;
}

export function configureDeferredInit(args: {
  cacheSignature: string | null;
  innerFile: File;
  modelId: string;
  filterState: FilterState;
  applyShareLink: boolean;
}): void {
  lastCacheSignature = args.cacheSignature;
  lastInnerFile = args.innerFile;
  lastModelId = args.modelId;
  pendingFilterState = args.filterState;
  shareLinkPending = args.applyShareLink;
  metaPromise = null;
  treePromise = null;
  revisionPromise = null;
}

export function ensureRevisionSnapshot(): Promise<void> {
  if (!lastModelId) {
    return Promise.resolve();
  }
  if (state.currentIfcRevision) {
    return Promise.resolve();
  }
  if (!revisionPromise) {
    revisionPromise = (async () => {
      // Если источник — уже сконвертированный .frag (например, пришёл из СОД),
      // его НЕЛЬЗЯ парсить через web-ifc: это бинарь Fragments, а не IFC-текст,
      // и OpenModel улетает в bad_alloc → Aborted (плюс лишняя нагрузка на старте
      // и риск повредить экземпляр web-ifc). Снимок строим напрямую из модели.
      const isFragSource = !!lastInnerFile && /\.frag$/i.test(lastInnerFile.name);

      // Для IFC-источника текущую ревизию строим из исходного файла через web-ifc —
      // ровно тем же путём, что и базовую (buildRevisionSnapshotFromFile). Это
      // гарантирует одинаковую популяцию сущностей и формат fingerprint у обеих
      // сторон, иначе сравнение идентичных моделей даёт ложные added/removed.
      if (lastInnerFile && !isFragSource) {
        try {
          const snap = await buildRevisionSnapshotFromFile(lastInnerFile);
          if (snap.size > 0) {
            state.currentIfcRevision = snap;
            return;
          }
        } catch (fileError) {
          console.warn("Revision snapshot (web-ifc) failed, trying model.", fileError);
        }
      }
      try {
        state.currentIfcRevision = await buildRevisionSnapshotFromModel(lastModelId!);
      } catch (revError) {
        console.warn("Revision snapshot failed, regex fallback.", revError);
        if (lastInnerFile && !isFragSource) {
          try {
            const rawIfcText = await lastInnerFile.text();
            state.currentIfcRevision = parseIfcRevisionSnapshot(rawIfcText);
          } catch (_) {
            state.currentIfcRevision = null;
          }
        }
      }
    })();
  }
  return revisionPromise;
}

export function ensureElementMeta(showProgress = false): Promise<void> {
  if (!state.ifcModel) {
    return Promise.resolve();
  }
  if (state.elementMeta.size > 0) {
    setFilterControlsEnabled(true);
    return Promise.resolve();
  }
  if (!metaPromise) {
    metaPromise = (async () => {
      if (showProgress) {
        setStatus("Анализ IFC метаданных для фильтров...");
        setProgress(0, "Анализ метаданных IFC…");
      }
      await buildElementMetaCache(lastCacheSignature, (pct) => {
        if (showProgress) {
          setProgress(pct, `Анализ метаданных IFC — ${pct}%`);
        }
      });
      applyFilterStateToUi(pendingFilterState);
      updateTransparentSpaceIdsFromMeta();
      rebuildMeshVisibility();
      updateSearchUiState();
      syncColorRuleUi();
      refreshPlanUi();
      if (showProgress) {
        hideProgress();
      }
    })().catch((err) => {
      metaPromise = null;
      throw err;
    });
  }
  return metaPromise;
}

export function ensureIfcTree(showProgress = false): Promise<void> {
  if (!state.ifcModel) {
    return Promise.resolve();
  }
  if (state.ifcTree) {
    renderIfcTree();
    return Promise.resolve();
  }
  if (!treePromise) {
    treePromise = (async () => {
      if (showProgress) {
        setStatus("Построение дерева пространственной структуры...");
        setProgress(null, "Построение дерева…");
      }
      await buildIfcTree();
      if (showProgress) {
        hideProgress();
      }
    })().catch((err) => {
      treePromise = null;
      throw err;
    });
  }
  return treePromise;
}

/**
 * Вызывается при переключении секций боковой панели (data-section).
 *
 * Нумерация разделов после реорганизации бокового меню (12 → 8, см.
 * index.html):
 *   1 — Модель · 2 — Выделение (подвкладки: инфо+примечания / свойства,
 *   были разделами 2 и 8) · 3 — Линейка · 4 — Поиск и фильтры (были 4 и 11)
 *   · 5 — Сравнение · 6 — Дерево (был 7) · 7 — Визуализация (подвкладки:
 *   разрезы / виды / цвета, были 9, 10, 12) · 8 — Настройки (был 6).
 *
 * Раздел объединяет несколько прежних панелей — если хотя бы одной из его
 * подвкладок нужны метаданные/дерево/виды, инициализируем это при входе в
 * раздел (сами функции идемпотентны и дешёвы при повторном вызове).
 */
export function onSideSectionActivated(sectionId: string): void {
  const sid = String(sectionId);
  if (sid === "4") {
    // Подвкладки «Поиск» и «Фильтр» — обеим нужен elementMeta.
    void ensureElementMeta(true);
  }
  if (sid === "5") {
    void ensureRevisionSnapshot();
  }
  if (sid === "6") {
    void ensureIfcTree(true);
  }
  if (sid === "7") {
    // Подвкладки «Виды» и «Цвета» соответственно.
    loadViewsForCurrentFile();
    void ensureElementMeta(false);
  }
  if (sid === "2") {
    // Подвкладка «Инфо» — примечания; подвкладка «Свойства» — properties-panel.
    void ensureElementMeta(false).then(() => {
      loadAnnotationsForCurrentFile();
      refreshAnnotationMarkers();
      updateAnnotationUi();
      void refreshPropertiesPanel();
    });
  }
}

/** Фоновые задачи после первого кадра (не блокируют открытие модели). */
export function startBackgroundInit(): void {
  resetFilterUiAndState();
  loadAnnotationsForCurrentFile();
  updateAnnotationUi();

  // Если на момент загрузки модели уже открыта секция, которой нужны
  // метаданные/дерево (фильтры, свойства, дерево и т.п.) — запускаем её
  // инициализацию сразу. Иначе ленивая загрузка срабатывает только при
  // переключении секций, и блок (например, фильтрация) остаётся пустым.
  const activeSection =
    document.querySelector<HTMLElement>(".side-rail-panel.is-active")?.dataset.panel ||
    localStorage.getItem(SIDE_RAIL_SECTION_KEY) ||
    "1";
  onSideSectionActivated(activeSection);

  if (shareLinkPending) {
    void Promise.all([ensureElementMeta(false), ensureRevisionSnapshot()]).then(() => {
      tryApplyShareLinkFromLocationHash();
    });
  }

  requestAnimationFrame(() => {
    void ensureRevisionSnapshot();
  });
}

export function primeFiltersOnApply(): Promise<void> {
  return ensureElementMeta(true);
}
